import { Button, Input } from '@/components/ui';
import { TextArea } from '@/components/ui/Textarea';
import { DatePicker } from '@/components/ui/DatePicker';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Contract } from '@console/utils';
import { addDays } from 'date-fns';

const renewContractSchema = (t: any, originalContract: Contract) =>
  z
    .object({
      startDate: z.date({
        required_error: t('contracts.renewForm.startDateRequired'),
        invalid_type_error: t('contracts.form.invalidDate'),
      }),
      endDate: z.date({
        required_error: t('contracts.renewForm.endDateRequired'),
        invalid_type_error: t('contracts.form.invalidDate'),
      }),
      contractedHours: z
        .string()
        .optional()
        .refine((val) => !val || (!isNaN(Number(val)) && Number(val) > 0), {
          message: t('contracts.form.invalidHours'),
        }),
      monthlyHoursLimit: z
        .string()
        .optional()
        .refine((val) => !val || (!isNaN(Number(val)) && Number(val) >= 0), {
          message: t('contracts.form.invalidHours'),
        }),
      minDebitTime: z
        .string()
        .optional()
        .refine((val) => !val || (!isNaN(Number(val)) && Number(val) > 0), {
          message: t('contracts.form.invalidMinutes'),
        }),
      renewalNotes: z.string().optional(),
    })
    .refine((data) => data.endDate > data.startDate, {
      message: t('contracts.form.endDateAfterStartDate'),
      path: ['endDate'],
    })
    .refine(
      (data) => {
        if (!originalContract.endDate) return true;
        const originalEndDate = new Date(originalContract.endDate);
        return data.startDate > originalEndDate;
      },
      {
        message: t('contracts.renewForm.startDateAfterOriginalEndDate'),
        path: ['startDate'],
      }
    );

export type RenewContractFormData = z.infer<ReturnType<typeof renewContractSchema>>;

export interface RenewContractFormProps {
  originalContract: Contract;
  onSubmit: (data: RenewContractFormData) => Promise<void>;
  isLoading: boolean;
  onCancel: () => void;
}

export function RenewContractForm({
  originalContract,
  onSubmit,
  isLoading,
  onCancel,
}: RenewContractFormProps) {
  const { t } = useTranslation();
  const schema = renewContractSchema(t, originalContract);

  // Calculate suggested start date (day after original contract ends)
  const suggestedStartDate = originalContract.endDate
    ? addDays(new Date(originalContract.endDate), 1)
    : undefined;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RenewContractFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      startDate: suggestedStartDate,
      endDate: undefined,
      contractedHours: originalContract.contractedHours.toString(),
      monthlyHoursLimit: originalContract.monthlyHoursLimit
        ? originalContract.monthlyHoursLimit.toString()
        : '',
      minDebitTime: originalContract.minDebitTime ? originalContract.minDebitTime.toString() : '15',
      renewalNotes: '',
    },
  });

  const handleFormSubmit = async (data: any) => {
    await onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="gap-md flex flex-col">
      <div className="gap-md grid grid-cols-2">
        <DatePicker
          label={t('contracts.renewForm.newStartDate')}
          placeholder={t('contracts.renewForm.newStartDatePlaceholder')}
          value={watch('startDate')}
          onChange={(date) => setValue('startDate', date as Date)}
          error={errors.startDate?.message}
          inputDisabled={isLoading}
          disabled={
            originalContract.endDate
              ? { before: addDays(new Date(originalContract.endDate), 1) }
              : undefined
          }
        />
        <DatePicker
          label={t('contracts.renewForm.newEndDate')}
          placeholder={t('contracts.renewForm.newEndDatePlaceholder')}
          value={watch('endDate')}
          onChange={(date) => setValue('endDate', date as Date)}
          error={errors.endDate?.message}
          inputDisabled={isLoading}
          disabled={watch('startDate') ? { before: watch('startDate') as Date } : undefined}
        />
      </div>

      <div className="gap-md grid grid-cols-2">
        <Input
          {...register('contractedHours')}
          type="number"
          label={t('contracts.renewForm.contractedHours')}
          placeholder={t('contracts.renewForm.contractedHoursPlaceholder')}
          error={errors.contractedHours?.message}
          disabled={isLoading}
        />
        <Input
          {...register('monthlyHoursLimit')}
          type="number"
          label={t('contracts.renewForm.monthlyHoursLimit')}
          placeholder={t('contracts.renewForm.monthlyHoursLimitPlaceholder')}
          error={errors.monthlyHoursLimit?.message}
          disabled={isLoading}
        />
      </div>

      <Input
        {...register('minDebitTime')}
        type="number"
        label={t('contracts.renewForm.minDebitTime')}
        placeholder={t('contracts.renewForm.minDebitTimePlaceholder')}
        error={errors.minDebitTime?.message}
        disabled={isLoading}
      />

      <TextArea
        {...register('renewalNotes')}
        label={t('contracts.renewForm.renewalNotes')}
        placeholder={t('contracts.renewForm.renewalNotesPlaceholder')}
        error={errors.renewalNotes?.message}
        disabled={isLoading}
        rows={3}
      />

      <div className="p-md rounded-lg border border-yellow-200 bg-yellow-50">
        <div className="text-sm text-yellow-800">
          <strong>{t('contracts.renewForm.warning')}:</strong>
          <ul className="mt-xs ml-4 list-disc space-y-1">
            <li>{t('contracts.renewForm.warningPoint1')}</li>
            <li>{t('contracts.renewForm.warningPoint2')}</li>
            <li>{t('contracts.renewForm.warningPoint3')}</li>
          </ul>
        </div>
      </div>

      <footer className="gap-sm mt-xl flex self-end">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={isLoading}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" loading={isLoading} variant="primary">
          {t('contracts.renewForm.renewContract')}
        </Button>
      </footer>
    </form>
  );
}
